import type { ExportJob } from "@dramapie/shared";

import { EXPORT_POLL_INTERVAL_MS, EXPORT_POLL_MAX_ATTEMPTS, isTerminalExport } from "./m4";

/**
 * 导出任务轮询（契约 M4 §3）：`POST /exports` 只回 `exportId`，
 * 完成信号靠 `GET /exports/:id`，2s 一次、最多 60 次。
 *
 * 抽成独立函数是为了能被单测：`sleep` / `intervalMs` / `maxAttempts` 都可注入，
 * 页面里用默认的 2s × 60，测试里用 0ms 直接跑完。
 */

export interface PollExportOptions {
  /** 第几次（从 1 开始）取任务详情。 */
  fetchOnce: (attempt: number) => Promise<ExportJob>;
  intervalMs?: number;
  maxAttempts?: number;
  sleep?: (ms: number) => Promise<void>;
  onUpdate?: (job: ExportJob, attempt: number) => void;
  /** 放弃条件：默认 succeeded / failed 即终止。 */
  isDone?: (job: ExportJob) => boolean;
  signal?: AbortSignal;
}

export type PollExportResult =
  | { timedOut: false; attempts: number; job: ExportJob }
  | { timedOut: true; attempts: number; job: ExportJob | null };

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function abortError(): Error {
  // DOMException 在 jsdom/浏览器都有；Node 18 也有全局 DOMException。
  if (typeof DOMException === "function") {
    return new DOMException("导出轮询已取消", "AbortError");
  }
  const error = new Error("导出轮询已取消");
  error.name = "AbortError";
  return error;
}

/**
 * 立即取第一次（省一次 2s 等待），之后每 `intervalMs` 取一次。
 * 非终止状态超过次数就返回 `timedOut: true`，让调用方给「稍后重试」而不是假装完成。
 */
export async function pollExportJob({
  fetchOnce,
  intervalMs = EXPORT_POLL_INTERVAL_MS,
  maxAttempts = EXPORT_POLL_MAX_ATTEMPTS,
  sleep = defaultSleep,
  onUpdate,
  isDone = (job) => isTerminalExport(job.status),
  signal,
}: PollExportOptions): Promise<PollExportResult> {
  const limit = Math.max(1, Math.floor(maxAttempts));
  let last: ExportJob | null = null;

  for (let attempt = 1; attempt <= limit; attempt += 1) {
    if (attempt > 1) await sleep(intervalMs);
    if (signal?.aborted) throw abortError();

    const job = await fetchOnce(attempt);
    last = job;
    onUpdate?.(job, attempt);
    if (isDone(job)) return { timedOut: false, attempts: attempt, job };
  }

  return { timedOut: true, attempts: limit, job: last };
}
